import React from 'react'
import {compose, setDisplayName, withHandlers, withPropsOnChange, withStateHandlers} from "recompose";
import {ifElse, always, equals} from "ramda";
import {connect} from "react-redux";

import { nextDropdown } from '../../../../../../actions/dropdown'
import { DropdownMenu } from "../../../../../Shared/DropdownMenu/DropdownMenu";

import './Notes.css'
import {getLocaleString} from "../../../../../../locale";
import {formatDate, createExportFile, getBookmarkPage} from "../../../../../../utilities/utilities";
import {getPdtPosition} from "../../../../../../utilities/pdfUtilities";
import {getFb2Position} from "../../../../../../utilities/fb2Utilities";
import {getCfi} from "../../../../../../utilities/epubUtilities";
import { Scrollbars } from 'react-custom-scrollbars';
import {highlightColors} from "../../../../../../constants";

const DROPDOWN_ID = 'NOTES'
const isActive = equals(DROPDOWN_ID)

const toggleMenu = ({activeDropdown, nextDropdown}) => ({isInner = false}) => ifElse(
  equals(true),
  () => nextDropdown(null),
  () => ifElse(
    equals(false),
    () => nextDropdown(DROPDOWN_ID),
    always(null)
  )(isInner)
)(isActive(activeDropdown))

const mapStateToProps = state => ({
  activeDropdown: state.activeDropdown,
  notes: state.reader.highlights,
  bookInfo: state.reader.bookInfo,
  language: state.reader.userData.language
})

const mapDispatchToProps = (dispatch) => ({
  nextDropdown: (id) => dispatch(nextDropdown(id))
})

const handlers = withHandlers({
  toggleMenu
})

const stateHandlers = {
  exportNotes: (state, {rendition, notes, bookInfo: {title}, language}) => () => {
    let content = '<?xml version="1.0" encoding="UTF-8"?>\n'
    content += '<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Strict//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-strict.dtd">'
    content += '<html xmlns="http://www.w3.org/1999/xhtml" xml:lang="'+language+'">\n'
    content += '<head>\n'
    content += '<meta name="generator" content="PocketBook Bookmarks Export">\n'
    content += '</meta>\n'
    content += '<title>'+title+'</title>\n'
    content += '<style type="text/css"><!--/*--><![CDATA[/*><!--*/\n'
    content += 'div.bookmark{margin-bottom: 10px;}div.bm-color-none{background-color:#eae6dc}div.bm-color-pink{background-color:#ecd6ea}div.bm-color-blue{background-color:#d2ebff}div.bm-color-red{background-color:#fcc1c1}div.bm-color-yellow{background-color:#fff3b2}div.bm-color-green{background-color:#d8f4cf}div.bm-delim{background-color:#d6d2c9;height:1px;width:100%;border-bottom:1px solid #fffaec}p.bm-page:before{content:open-quote}p.bm-page:after{content:close-quote}p.bm-page:lang(ru){quotes:"стр. № " "." "\'" "\'"}p.bm-page:lang(uk){quotes:"стор. № " "." "\'" "\'"}p.bm-page:lang(de){quotes:"Seite Nr " "." "\'" "\'"}p.bm-page:lang(fr){quotes:"page No " "." "\'" "\'"}p.bm-page:lang(zh){quotes:"頁 " "." "\'" "\'"}p.bm-page{quotes:"page # " "." "\'" "\'";color:#999;font-size:14pt;font-family:Geneva,Arial,Helvetica,sans-serif}div.bookmark{font-size:14pt;text-align:justify;color:#222;padding:10px 40px}div.bm-text{font-family:Geneva,Arial,Helvetica,sans-serif}div.bm-note{color:#999;font-style:oblique;position:relative;padding:5px 20px;background:#fffaec;-webkit-border-radius:5px;border-radius:5px;border:#d3d3d3 solid 1px}div.bm-note:after{content:\'\';position:absolute;border-color:#fffaec transparent;display:block;width:0;z-index:2;top:-14px;left:51px;height:0;border-bottom:15px solid #fffaec;border-right:24px solid transparent}div.bm-note:before{content:\'\';position:absolute;display:block;width:0;z-index:1;top:-15px;left:50px;height:0;border-bottom:15px solid #d3d3d3;border-right:24px solid transparent}div.bm-image{text-align:center}\n'
    content += '/*]]>*/--></style>\n'
    content += '</head>\n'
    content += '<body>\n'

    notes.map(note => {
      let color = note.color ? note.color.value : 'none'
      let id = note.orig_id ? note.orig_id : note.uuid
      content += '<div id="'+id+'" class="bookmark bm-color-'+color+'">\n'
      let page = getBookmarkPage(rendition, note)
      if (page) content += '<p class="bm-page">'+page+'</p>\n'
      content += '<div class="bm-text">\n'
      content += '<p>'+note.quotation.text+'</p>\n'
      content += '</div>\n'
      if (note.note) {
        content += '<div class="bm-note">\n'
        content += '<p>'+note.note.text+'</p>\n'
        content += '</div>\n'
      }
      content += '</div>\n'
      return note
    })

    content += '</body>\n'
    content += '</html>'

    createExportFile(title+'.html', content)
  },
  onFilter: ({filter}) => (e, value) => {
    e.stopPropagation()
    return {filter: filter === value ? 'none' : value}
  }
}

const state = {
  filter: 'none'
}

export const enhance = compose(
  setDisplayName('Notes'),
  connect(mapStateToProps, mapDispatchToProps),
  withPropsOnChange(['activeDropdown'], ({activeDropdown}) => ({
    active: isActive(activeDropdown)
  })),
  handlers,
  withStateHandlers(state, stateHandlers)
)

const NotesFilter = ({onFilter, filter}) => (
  <div className="Filter">
    <p className="Filter__title">{getLocaleString('reader.filter')}:</p>
    <div className="Filter__filters">
      {Object.keys(highlightColors).map(value => <Filter key={value} value={value} filter={filter} onFilter={onFilter}/>)}
      <span className={`color default${filter === 'none' ? ' checked' : ''}`} onClick={e => onFilter(e, 'none')}>
        <span className="red"></span>
        <span className="green"></span>
        <span className="blue"></span>
        <span className="yellow"></span>
      </span>
    </div>
  </div>
)

const Filter = ({value, filter, onFilter}) => (
  <span className={`color ${value}${filter === value ? ' checked' : ''}`} onClick={e => onFilter(e, value)}></span>
)

const NotesList = ({notes, toggleMenu, onLoadHighlight, onDeleteNote, filter}) => (
  <ul className="notes">
    {notes.length > 0 ? notes.sort((a,b) => {
      if (a.mark && b.mark) {
        if (a.mark.created && b.mark.created) {
          if (a.mark.created < b.mark.created)
            return 1;
          if (a.mark.created > b.mark.created)
            return -1;
        } else {
          if (a.mark.updated < b.mark.updated)
            return 1;
          if (a.mark.updated > b.mark.updated)
            return -1;
        }
      }
      return 0;
    }).map((note, index) => {
      if (!note.color) {
        note.color = {
          value: note.type.value === 'note' ? 'red' : 'yellow'
        }
      }
      if (filter !== 'none' && filter !== note.color.value) return ''
      return <NoteItem key={index} note={note} toggleMenu={toggleMenu}
                       onLoadHighlight={onLoadHighlight} onDeleteNote={onDeleteNote}/>
    }) : ''
    }
  </ul>
)

const getHighlightPage = (note) => {
  let page = false
  const anchor = note.quotation.begin
  if (anchor.indexOf('pdfloc') > -1) {
    page = getPdtPosition(anchor)
  } else if (anchor.indexOf('cfi') > -1) {
    const cfi = getCfi(anchor)
    if (cfi) page = true
  } else {
    page = getFb2Position(anchor)
  }
  return page
}

const limitedNoteText = (text) => text.length > 100 ? text.slice(0, 100) + '...' : text.slice(0, 100)
const getColor = (note) => {
  if (note.type.value === 'note') {
    return note.color ? note.color.value : 'red'
  } else {
    return note.color ? note.color.value : 'yellow'
  }
}

const NoteItem = ({note, toggleMenu, onLoadHighlight, onDeleteNote}) => {

  if (!note.quotation || !note.quotation.begin || !note.quotation.end) return ''
  let page = getHighlightPage(note)
  if (!note.mark || !page) return ''

  return <li className={`note-item ${getColor(note)}`} onClick={(e) => {
    e.stopPropagation()
    toggleMenu(false);
    onLoadHighlight(note.quotation.begin)
  }}>
    <span className="note-item-date">{note.mark ? note.mark.created ? formatDate(note.mark.created) : '' : ''}</span>
    <span
      className={`note-item-text ${note.quotation ? !note.quotation.text ? 'hide' : '' : ''}`}>{note.quotation ? note.quotation.text ? limitedNoteText(note.quotation.text) : '' : ''}</span>
    <span className={`note-item-note ${!note.note ? 'hide' : ''}`}>{note.note ? limitedNoteText(note.note.text) : ''}</span>
    <button className="note-delete icon-btn" onClick={(e) => {
      e.stopPropagation();
      onDeleteNote(note, true)
    }}></button>
  </li>
}

const View = ({toggleMenu, active, notes, onLoadHighlight, onDeleteNote, exportNotes, onFilter, filter}) => (
  <div className="Notes__wrap">
    <button className="notes-btn icon-btn" onClick={toggleMenu} data-active={active}></button>
    <DropdownMenu active={active}>
      <div className="Notes__header Dropdown__header">
        <span className="Notes__header-text">{getLocaleString('reader.notesTitle')}</span>
        <button className="btn btn-close-white icon-btn" onClick={toggleMenu} data-active={active}></button>
      </div>
      <div className="Notes__content limit-content-height">
        {notes.length > 0 ? <NotesFilter onFilter={onFilter} filter={filter}/> : ''}
        <Scrollbars style={{ width: 315, height: 382 }}>
          <NotesList notes={notes} toggleMenu={toggleMenu} filter={filter}
                     onLoadHighlight={onLoadHighlight} onDeleteNote={onDeleteNote}/>
        </Scrollbars>
        {notes.length > 0 ?
            <div className="Export__wrap">
              <button className="btn btn-export" onClick={exportNotes}>{getLocaleString('reader.export')}</button>
            </div>
          : ''}
      </div>
    </DropdownMenu>
  </div>
)

export const Notes = enhance(View)


// WEBPACK FOOTER //
// ./src/components/Reader/components/Navigation/components/Notes/Notes.js